/**
 * render/bridge.ts — Type Bridge
 *
 * The two sides of Hearth speak different dialects of the same language.
 * engine/state.ts uses uppercase compass directions ("N", "NE", ...) and
 * a nine-value AnimState. character/composer.ts uses lowercase ("n", "ne", ...)
 * and six animation states. Here is where those dialects are translated
 * so the renderer can speak to both systems without static everywhere else.
 *
 * Nothing complex lives here. Conversions, maps, and a sensible fallback
 * appearance so the character is never naked.
 */

import type {
  Direction as ComposerDirection,
  AnimState as ComposerAnimState,
  CharacterAppearance as ComposerAppearance,
} from "../character/composer.js";
import { SKIN_TONES, HAIR_COLORS, ITEM_COLORS } from "../character/composer.js";
import type { CharacterAppearance as StateAppearance } from "../engine/state.js";

// ─── Direction Types ──────────────────────────────────────────────────────────

/** The eight compass directions as engine/state.ts uses them — uppercase. */
export type StateDirection = "N" | "NE" | "E" | "SE" | "S" | "SW" | "W" | "NW";

/** The animation states engine/state.ts tracks. */
export type StateAnimState =
  | "idle" | "walk" | "sit" | "interact" | "emote"
  | "wave" | "dance" | "type" | "sleep"
  | "combat_idle" | "combat_attack";

// ─── Direction Conversion ─────────────────────────────────────────────────────

/**
 * Convert an uppercase engine direction to the lowercase form composer expects.
 *
 * "N" → "n", "SE" → "se", etc. Simple but necessary — having it centralised
 * means a future rename can't silently break half the codebase.
 */
export function toComposerDir(dir: string): ComposerDirection {
  return dir.toLowerCase() as ComposerDirection;
}

// ─── AnimState Conversion ─────────────────────────────────────────────────────

/**
 * Collapse the nine-value engine AnimState to the six-value composer AnimState.
 *
 * The composer has no "wave", "type", or "sleep" states — we fold them into
 * the nearest visual equivalent: wave→emote, type→interact, sleep→sit.
 * This is intentional compression, not a loss. The composer layers provide
 * enough visual variety within each state.
 */
export function toComposerAnim(anim: string): ComposerAnimState {
  const map: Record<string, ComposerAnimState> = {
    idle:     "idle",
    walk:     "walk",
    sit:      "sit",
    interact: "interact",
    emote:    "emote",
    dance:    "dance",
    wave:     "emote",     // wave is an emote in disguise
    type:     "interact",  // typing is focused interaction
    sleep:    "sit",       // sleep pose is a sitting variant
    device:        "device",        // holding up hPhone or hPad
    device_walk:   "device_walk",   // walking slowly while holding phone
    device_sit:    "device_sit",    // sitting while holding device
    combat_idle:   "combat_idle",   // combat ready stance
    combat_attack: "combat_attack", // attack swing
  };
  return map[anim] ?? "idle";
}

// ─── Appearance Conversion ────────────────────────────────────────────────────

/**
 * Module-level store for the player's chosen ComposerAppearance.
 * Set by the character creation screen, read by the room renderer.
 */
let _customAppearance: ComposerAppearance | null = null;

/**
 * Monotonic version counter — incremented every time the composer appearance
 * changes. The room renderer compares this against its cached version to
 * detect when it needs to sync the CharacterState and invalidate the cache.
 */
let _appearanceVersion = 0;

/** Return the current appearance version counter. */
export function getAppearanceVersion(): number {
  return _appearanceVersion;
}

/**
 * Store a customized ComposerAppearance so future calls to
 * toComposerAppearance() return it instead of the default.
 */
export function setComposerAppearance(app: ComposerAppearance): void {
  _customAppearance = { ...app };
  _appearanceVersion++;
}

/**
 * Update individual properties on the stored composer appearance.
 * Used by the wardrobe app to apply changes without rebuilding the entire object.
 * Bumps the version counter so the room renderer detects the change.
 */
export function patchComposerAppearance(patch: Partial<ComposerAppearance>): void {
  if (!_customAppearance) _customAppearance = toComposerAppearance();
  Object.assign(_customAppearance, patch);
  _appearanceVersion++;
}

/**
 * Build a ComposerAppearance matching the starter character palette.
 *
 * If the player has already customized via the character creation screen,
 * returns their chosen look. Otherwise returns a warm default that looks
 * at home in the starter room.
 */
export function toComposerAppearance(): ComposerAppearance {
  if (_customAppearance) return { ..._customAppearance };
  return {
    bodyType:    1,   // average build
    skinTone:    3,   // warm tan — index into composer's SKIN_TONES
    hairStyle:   3,   // medium, slightly shaggy
    hairColor:   1,   // deep brown — index into HAIR_COLORS
    eyeStyle:    0,   // default friendly eyes
    eyeColor:    2,   // warm hazel
    topStyle:    0,   // simple cozy top
    topColor:    16,  // dusty rose — index 16 in ITEM_COLORS
    bottomStyle: 0,   // simple trousers
    bottomColor: 4,   // dark slate
    shoeStyle:   0,   // plain boots
    shoeColor:   4,   // dark brown
    hatStyle:    -1,  // no hat
    hatColor:    0,
    heldItem:    -1,  // nothing held
    jacketStyle: -1,  // no jacket
    jacketColor: 0,
    skinDetail:     -1,  // no freckles/marks
    accessoryStyle: -1,  // no accessory
    accessoryColor: 0,
    auraStyle:      -1,  // no aura
    wingsStyle:     -1,
    wingsColor:     0,
    capeStyle:      -1,
    capeColor:      0,
    backpackStyle:  -1,
    backpackColor:  0,
    facialHairStyle: -1,
    facialHairColor: 3,
    makeupStyle:    -1,
    makeupColor:    0,
    lipStyle:       -1,
    lipColor:       0,
    expression:     "neutral",
    bodyArchetype1: "soft",
    bodyArchetype2: "lithe",
    archetypeBlend: 0.3,
    bodyScale:      2,
    shoulderAdjust: 2,
    waistAdjust:    2,
    hipAdjust:      2,
    legGap:         2,
    nude:           false,
    bodyGender:     "feminine",
    breastSize:     2,
    buttSize:       2,
    genitalSize:    2,
  };
}

// ─── Furniture ID Mapping ─────────────────────────────────────────────────────

/**
 * Map the string-based type identifiers from engine/state.ts FurniturePlacement
 * to the IDs registered in furniture.ts's registry.
 *
 * The state stores short design tokens ("bed", "desk") — the registry uses
 * richer namespaced IDs ("bed_cozy", "desk_study"). This map is the bridge.
 * Unknown types pass through unchanged; if the registry doesn't know them
 * either, the renderer falls back to a block draw gracefully.
 */
const FURNITURE_ID_MAP: Record<string, string> = {
  bed:          "bed_cozy",
  desk:         "desk_study",
  aeterna:      "aeterna_device",
  desk_chair:   "chair_wooden",
  rug_round:    "rug_woven",
  floor_lamp:   "floor_lamp",
  potted_fern:  "potted_plant",
  bookshelf:    "bookshelf",
  fireplace:    "fireplace",
  sofa:         "sofa_oak",
  chair:        "chair_wooden",
};

/**
 * Convert a state-layer furniture type string to its registry ID.
 *
 * Falls through to the raw type string if no mapping exists — this lets
 * furniture that's already using registry IDs work without a mapping entry.
 */
export function toRegistryId(stateType: string): string {
  return FURNITURE_ID_MAP[stateType] ?? stateType;
}

// ─── Furniture Rotation ───────────────────────────────────────────────────────

/**
 * Convert a direction string to the 0-3 rotation index renderFurniture expects.
 *
 * The four furniture orientations in the renderer:
 *   0 = SE  (default facing, toward camera and right)
 *   1 = SW  (rotated 90° clockwise)
 *   2 = NW  (facing away from camera)
 *   3 = NE  (rotated 90° counter-clockwise)
 *
 * Accepts both the uppercase engine strings and lowercase composer strings.
 */
export function toFurnitureRotation(dir: string): 0 | 1 | 2 | 3 {
  const d = dir.toUpperCase();
  if (d === "E"  || d === "SE") return 0;
  if (d === "S"  || d === "SW") return 1;
  if (d === "W"  || d === "NW") return 2;
  return 3; // "N" | "NE" | anything else
}

// ─── NPC Appearance Bridge ──────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16) || 0,
    parseInt(h.substring(2, 4), 16) || 0,
    parseInt(h.substring(4, 6), 16) || 0,
  ];
}

function closestIndex(hex: string, palette: readonly string[]): number {
  const [r, g, b] = hexToRgb(hex);
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < palette.length; i++) {
    const [pr, pg, pb] = hexToRgb(palette[i]);
    const d = (r - pr) ** 2 + (g - pg) ** 2 + (b - pb) ** 2;
    if (d < bestDist) { bestDist = d; best = i; }
  }
  return best;
}

const HAIR_STYLE_MAP: Record<string, number> = {
  "none": -1, "bald": -1,
  "short_curly": 1, "short_spiky": 2, "medium_loose": 3,
  "long_braid": 4, "long_straight": 5, "pixie_cut": 0,
  "mohawk": 6, "ponytail": 7, "bun": 8,
};

/**
 * Convert an NPC's string-based StateAppearance into a numeric ComposerAppearance.
 */
export function npcToComposerAppearance(a: StateAppearance): ComposerAppearance {
  return {
    bodyType:    1,
    skinTone:    closestIndex(a.skinTone, SKIN_TONES),
    hairStyle:   HAIR_STYLE_MAP[a.hairStyle] ?? 3,
    hairColor:   closestIndex(a.hairColour, HAIR_COLORS),
    eyeStyle:    0,
    eyeColor:    2,
    topStyle:    0,
    topColor:    closestIndex(a.outfitTopColour, ITEM_COLORS),
    bottomStyle: 0,
    bottomColor: closestIndex(a.outfitBottomColour, ITEM_COLORS),
    shoeStyle:   0,
    shoeColor:   closestIndex(a.shoesColour, ITEM_COLORS),
    hatStyle:    -1,
    hatColor:    0,
    heldItem:    -1,
    jacketStyle: -1,
    jacketColor: 0,
    skinDetail:     -1,
    accessoryStyle: -1,
    accessoryColor: 0,
    auraStyle:      -1,
    wingsStyle:     -1,
    wingsColor:     0,
    capeStyle:      -1,
    capeColor:      0,
    backpackStyle:  -1,
    backpackColor:  0,
    facialHairStyle: -1,
    facialHairColor: 3,
    makeupStyle:    -1,
    makeupColor:    0,
    lipStyle:       -1,
    lipColor:       0,
    expression:     "neutral",
    bodyArchetype1: "lithe",
    bodyArchetype2: "lithe",
    archetypeBlend: 0,
    bodyScale:      2,
    shoulderAdjust: 2,
    waistAdjust:    2,
    hipAdjust:      2,
    legGap:         2,
    nude:           false,
    bodyGender:     "androgynous",
    breastSize:     2,
    buttSize:       2,
    genitalSize:    2,
  };
}
